import React, { useEffect, useState } from 'react';
import {
  Box, Paper, Typography, Stack, Button, TextField, Autocomplete,
  CircularProgress, Alert, Chip,
  Table, TableHead, TableRow, TableCell, TableBody,
} from '@mui/material';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { getAllStates, getCitiesByState } from '../services/referenceService';
import { getQrInsights, QrInsights as QrInsightsData } from '../services/qrBookingService';

// Default window: the last 30 days, matching the backend default.
const ymd = (d: Date) => d.toISOString().slice(0, 10);
const defaultFrom = () => { const d = new Date(); d.setDate(d.getDate() - 30); return ymd(d); };

// seconds → "2m 5s" / "45s" / "1h 3m" (claim-time display).
const fmtSecs = (sec: number | null): string => {
  if (sec == null) return '—';
  if (sec < 60) return `${Math.round(sec)}s`;
  if (sec < 3600) return `${Math.floor(sec / 60)}m ${Math.round(sec % 60)}s`;
  return `${Math.floor(sec / 3600)}h ${Math.round((sec % 3600) / 60)}m`;
};

const rupees = (n: number | null | undefined) =>
  n == null ? '—' : `₹${Number(n).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

type CityOption = { label: string; value: string };

// Small KPI tile (mirrors the compact stat cards used elsewhere in the dashboard).
const Kpi: React.FC<{ label: string; value: React.ReactNode; sub?: string; color?: string }> = ({ label, value, sub, color }) => (
  <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: 11 }}>
      {label}
    </Typography>
    <Typography variant="h5" sx={{ fontWeight: 800, color: color || 'text.primary', lineHeight: 1.2, mt: 0.5 }}>
      {value}
    </Typography>
    {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
  </Paper>
);

// One horizontal funnel bar (width proportional to `count/booked`).
const FunnelBar: React.FC<{ label: string; count: number; booked: number; color: string; pct?: string }> = ({ label, count, booked, color, pct }) => {
  const widthPct = booked > 0 ? Math.max(2, (count / booked) * 100) : 0;
  return (
    <Box sx={{ mb: 1 }}>
      <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.25 }}>
        <Typography variant="caption" sx={{ fontWeight: 700 }}>{label}</Typography>
        <Typography variant="caption" color="text.secondary">{count.toLocaleString('en-IN')}{pct ? ` · ${pct}` : ''}</Typography>
      </Stack>
      <Box sx={{ height: 22, bgcolor: 'action.hover', borderRadius: 1, overflow: 'hidden' }}>
        <Box sx={{ width: `${widthPct}%`, height: '100%', bgcolor: color, transition: 'width .3s' }} />
      </Box>
    </Box>
  );
};

const QrInsights: React.FC = () => {
  const [from, setFrom] = useState(defaultFrom());
  const [to, setTo] = useState(ymd(new Date()));

  const [statesList, setStatesList] = useState<any[]>([]);
  const [citiesList, setCitiesList] = useState<any[]>([]);
  const [selectedStateCode, setSelectedStateCode] = useState<string | null>(null);
  const [city, setCity] = useState<string>('');

  const [data, setData] = useState<QrInsightsData | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getAllStates()
      .then((res) => { if (res.data?.success) setStatesList(res.data.states); })
      .catch(() => { /* non-fatal — city filter is optional */ });
  }, []);

  const handleStateChange = async (stateCode: string | null) => {
    setSelectedStateCode(stateCode);
    setCitiesList([]);
    setCity('');
    if (stateCode) {
      try {
        const res = await getCitiesByState(stateCode);
        if (res.data?.success) setCitiesList(res.data.cities);
      } catch { /* ignore */ }
    }
  };

  const load = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await getQrInsights({ from, to, cityId: city || undefined });
      setData(res.data.data);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to load QR insights');
    } finally {
      setBusy(false);
    }
  };

  // Load the default 30-day window once on mount.
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const cityOptions: CityOption[] = citiesList.map((c) => ({ label: c.name, value: c.name }));
  const currentCity = city ? (cityOptions.find((o) => o.value === city) || { label: city, value: city }) : null;

  const f = data?.funnel;
  const trendData = (data?.byDay || []).map((d) => ({ day: d.day.slice(5), booked: d.booked, completed: d.completed }));

  return (
    <Box sx={{ width: '100%', maxWidth: 1200, mx: 'auto' }}>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1 }}>
        <QrCode2Icon color="primary" />
        <Typography variant="h5" sx={{ fontWeight: 800 }}>QR Booking Insights</Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        The QR web-booking funnel, revenue and driver adoption. Read-only, derived from <code>qr_web</code> rides in the
        selected window. Leave the city blank for all cities.
      </Typography>

      {/* --- FILTERS --- */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
          <TextField label="From" type="date" size="small" InputLabelProps={{ shrink: true }} value={from} onChange={(e) => setFrom(e.target.value)} />
          <TextField label="To" type="date" size="small" InputLabelProps={{ shrink: true }} value={to} onChange={(e) => setTo(e.target.value)} />
          <Autocomplete
            size="small" sx={{ minWidth: 180 }}
            options={statesList}
            getOptionLabel={(o) => o.name || ''}
            value={statesList.find((s) => s.isoCode === selectedStateCode) || null}
            onChange={(_e, v) => handleStateChange(v ? v.isoCode : null)}
            renderInput={(params) => <TextField {...params} label="State (filter)" />}
          />
          <Autocomplete
            size="small" sx={{ minWidth: 200 }}
            options={cityOptions}
            getOptionLabel={(o) => o.label}
            value={currentCity}
            onChange={(_e, v) => setCity(v ? v.value : '')}
            renderInput={(params) => <TextField {...params} label="City (optional)" />}
          />
          <Button
            variant="contained" onClick={load} disabled={busy}
            startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
          >
            Load
          </Button>
        </Stack>
      </Paper>

      {error && <Alert severity="error" variant="outlined" sx={{ mb: 3 }}>{error}</Alert>}

      {data && f && (
        <>
          {/* --- KPI CARDS --- */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 2, mb: 3 }}>
            <Kpi label="Booked" value={f.booked.toLocaleString('en-IN')} sub={`${data.adoption.activeDrivers} active QR drivers`} />
            <Kpi label="Started" value={f.started.toLocaleString('en-IN')} sub={`${f.startedPct}% of booked`} color="#0288d1" />
            <Kpi label="Completed" value={f.completed.toLocaleString('en-IN')} sub={`${f.completedPct}% of started`} color="#2e7d32" />
            <Kpi label="Expired unclaimed" value={f.expiredUnclaimed.toLocaleString('en-IN')} sub={`${f.otherCancelled} other cancelled`} color="#d32f2f" />
            <Kpi label="Revenue (completed)" value={rupees(data.money.revenue)} />
            <Kpi label="Avg fare" value={rupees(data.money.avgFare)} sub={data.money.avgDistanceKm != null ? `avg ${data.money.avgDistanceKm} km` : undefined} />
            <Kpi label="Avg claim time" value={fmtSecs(data.health.avgClaimSecs)} sub={`median ${fmtSecs(data.health.medianClaimSecs)}`} />
            <Kpi label="QR tokens" value={data.adoption.tokens.toLocaleString('en-IN')} sub={`${data.adoption.tokensRevoked} revoked`} />
          </Box>

          {/* --- FUNNEL + DAILY TREND --- */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3, mb: 3 }}>
            <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>Funnel</Typography>
              <FunnelBar label="Booked" count={f.booked} booked={f.booked} color="#1976d2" />
              <FunnelBar label="Started" count={f.started} booked={f.booked} color="#0288d1" pct={`${f.startedPct}%`} />
              <FunnelBar label="Completed" count={f.completed} booked={f.booked} color="#2e7d32" pct={`${f.completedPct}% of started`} />
              <Stack direction="row" spacing={1} sx={{ mt: 2, flexWrap: 'wrap', gap: 1 }}>
                <Chip size="small" variant="outlined" color="error" label={`Expired unclaimed: ${f.expiredUnclaimed}`} />
                <Chip size="small" variant="outlined" label={`Other cancelled: ${f.otherCancelled}`} />
              </Stack>
            </Paper>

            <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>Daily trend</Typography>
              {trendData.length === 0 ? (
                <Typography variant="body2" color="text.secondary">No rides in this window.</Typography>
              ) : (
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={trendData} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="day" fontSize={11} />
                    <YAxis fontSize={11} allowDecimals={false} />
                    <RTooltip />
                    <Legend />
                    <Bar dataKey="booked" name="Booked" fill="#90caf9" />
                    <Bar dataKey="completed" name="Completed" fill="#2e7d32" />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </Paper>
          </Box>

          {/* --- BREAKDOWN TABLES --- */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3, mb: 3 }}>
            <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>By city</Typography>
              <BreakdownTable rows={data.byCity} head="City" />
            </Paper>
            <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>By ride type</Typography>
              <BreakdownTable rows={data.byType} head="Type" />
            </Paper>
          </Box>

          {/* --- TOP DRIVERS --- */}
          <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Top QR drivers (by completed rides)</Typography>
            {data.topDrivers.length === 0 ? (
              <Typography variant="body2" color="text.secondary">No completed QR rides in this window.</Typography>
            ) : (
              <Box sx={{ overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>#</TableCell>
                      <TableCell>Driver</TableCell>
                      <TableCell>Plate</TableCell>
                      <TableCell align="right">Completed</TableCell>
                      <TableCell align="right">Revenue</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.topDrivers.map((d, i) => (
                      <TableRow key={d.driverId} hover>
                        <TableCell>{i + 1}</TableCell>
                        <TableCell>{d.name || d.driverId.slice(0, 10)}</TableCell>
                        <TableCell>{d.plate || '—'}</TableCell>
                        <TableCell align="right"><strong>{d.completed}</strong></TableCell>
                        <TableCell align="right">{rupees(d.revenue)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            )}
          </Paper>
        </>
      )}

      {!data && !error && !busy && (
        <Typography color="text.secondary">Pick a range and load.</Typography>
      )}
    </Box>
  );
};

// Shared per-city / per-type table.
const BreakdownTable: React.FC<{ rows: { key: string; booked: number; completed: number; revenue: number }[]; head: string }> = ({ rows, head }) => {
  if (!rows.length) return <Typography variant="body2" color="text.secondary">No data.</Typography>;
  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>{head}</TableCell>
            <TableCell align="right">Booked</TableCell>
            <TableCell align="right">Completed</TableCell>
            <TableCell align="right">Revenue</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={r.key} hover>
              <TableCell>{r.key}</TableCell>
              <TableCell align="right">{r.booked}</TableCell>
              <TableCell align="right">{r.completed}</TableCell>
              <TableCell align="right">{r.revenue == null ? '—' : `₹${Number(r.revenue).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
};

export default QrInsights;
